import { ApiResponse } from '@/utils';
import { enhancedRequest } from '@/utils/enhancedRequest';
import { ModelListResponse } from '../types';

/**
 * 模型排序比较器：按 group_sort 降序（权重大的排前面）。
 *
 * 画布的图片 / 文本 / 视频三处模型选择器统一用它，保证展示顺序口径一致
 * （与 pv/其他页面既有实现 `b.group_sort - a.group_sort` 保持一致）。
 * 缺省值按 0 处理，避免后端未下发该字段时顺序错乱。
 */
export const byGroupSortDesc = <T extends { group_sort?: number }>(a: T, b: T): number =>
  (b.group_sort ?? 0) - (a.group_sort ?? 0);

/**
 * 模型类型枚举
 */
export enum ModelType {
  GENERAL = 'general',
  RE_FACE = 're-face',
  RE_WEAR = 're-wear',
}

/**
 * AMAI模型服务
 * 用于获取模型列表信息
 */
class ModelService {
  /**
   * 获取模型列表（带防重复请求和缓存）
   * @param typeTags 可选的模型类型过滤 text=文本模型; image=图片模型; video=视频模型
   * @param modelType 可选的模型类型枚举 general=通用模型; re-face=换脸模型; re-wear=换装模型
   * @param canvasShow 是否只取画布可展示的模型（拼 canvas_show=true）
   * @param forceRefresh 是否绕过缓存强制重取
   * @param page 页码（从 1 开始）。不传则后端返回该类型全部模型；
   *             传了则按后端单页上限分页（响应体只有 data.list，无 total/has_more），
   *             超出范围时 data 为空
   * @returns 模型列表
   */
  async getModelList(
    typeTags?: Array<'text' | 'image' | 'video'>,
    modelType?: string,
    canvasShow?: boolean,
    forceRefresh?: boolean,
    page?: number
  ): Promise<ApiResponse<ModelListResponse>> {
    const params: any = {};
    if (typeTags && typeTags.length > 0) {
      params.type_tags = typeTags;
    }
    if (modelType) {
      params.model_type = modelType;
    }
    if (canvasShow !== undefined) {
      params.canvas_show = canvasShow;
    }
    if (page !== undefined) {
      params.page = page;
    }

    // 模型列表变化不频繁，缓存30分钟，启用防重复请求
    // forceRefresh=true 时绕过缓存强制重取（成功后仍写回缓存供后续复用）
    //
    // ⚠ 分页请求（page>1）不走缓存、不做去重：缓存命中会直接返回而不发网络请求，
    //   而「加载更多」是用户主动触发的追加动作——一旦上一次的空响应（已到底）或旧分页
    //   被缓存 30 分钟，用户再点就会「没发请求也没变化」，表现为按钮点了没反应。
    const cacheable = page === undefined || page <= 1;
    return enhancedRequest.get<ModelListResponse>('/model/list', params, {
      useCache: cacheable,
      cacheTTL: 30 * 60 * 1000, // 30分钟缓存
      forceRefresh: forceRefresh ?? false,
      enableDeduplication: cacheable,
      requestKey: `model-list:${JSON.stringify(params)}`,
      retryCount: 2,
    });
  }
}

export default new ModelService();
